import { computed, inject, Injectable, signal } from '@angular/core';
import { JobsService } from './jobs.service';
import { TokenService } from './token.service';

interface UserState {
  isLoggedIn: boolean;
  role: 'super-admin' | 'admin' | 'user';
  availableCreds: number;
  reviewTourTaken: boolean;
  businessName: string;
  businessIconUrl: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class AppService {
  private readonly _jobsService = inject(JobsService)
  private readonly _tokenService = inject(TokenService)

  userState = signal<UserState>({
    isLoggedIn: false,
    role: 'user',
    availableCreds: 0,
    businessName: 'Business',
    reviewTourTaken: false,
    businessIconUrl: null
  });

  readonly isUserLoggedIn = computed(() => this.userState().isLoggedIn);
  readonly userRole = computed(() => this.userState().role);
  readonly availableCreds = computed(() => this.userState().availableCreds);
  readonly reviewTourTaken = computed(() => this.userState().reviewTourTaken);
  readonly businessIconUrl = computed(() => this.userState().businessIconUrl);

  initialUserState: UserState = {
    isLoggedIn: false,
    role: 'user',
    availableCreds: 0,
    reviewTourTaken: false,
    businessIconUrl: null,
    businessName: ''
  }

  constructor() {
    if (localStorage.getItem('reviewTourState')) {
    this.updateReviewTourState(true);
    }
    if (localStorage.getItem('jwt_access_token')) {
      this.loadCreds();
      this.updateUserState(true)
    }
    if (localStorage.getItem('role')) {
      this.updateUserRole(localStorage.getItem('role'))
    }
  }

  setUserProfile(data: {
    access_token: string,
    refresh_token: string,
    role: string
  }) {
    this._tokenService.saveToken(data.access_token);
    this._tokenService.saveRefreshToken(data.refresh_token);
    this.updateUserRole(data.role);
    this.updateUserState(true);
    this.loadCreds();
  }

  updateReviewTourState(state: boolean) {
    this.userState.update((item) => ({
      ...item,
      reviewTourTaken: state
    }));
    localStorage.setItem('reviewTourState', JSON.stringify('true'));
  }

  updateUserState(state: boolean) {
    this.userState.update((item) => ({
      ...item,
      isLoggedIn: state
    }))
  }

  updateUserRole(role: any) {
    localStorage.setItem('role', role)
    this.userState.update((item) => ({
      ...item,
      role
    }))
  }

  loadCreds() {
    this._jobsService.getAvailableCreds()
      .subscribe({
        next: (res: any) => {
          this.updateCreds(res?.data?.available_credits);
        },
        error: (err: any) => {
          console.log(err)
        }
      })
  }

  updateCreds(creds: number) {
    this.userState.update((data: any) => ({
      ...data,
      availableCreds: creds || 0
    }))
  }

  updateBusinessName(name: string) {
    this.userState.update((data) => ({
      ...data,
      businessName: name
    }))
  }

  updateBusinessIcon(url: string | null) {
    this.userState.update((data) => ({
      ...data,
      businessIconUrl: url
    }))
  }

  resetState() {
    localStorage.clear();
    this._tokenService.clearToken();
    this.userState.set(this.initialUserState)
  }
}
